/**
 * Bolsas de firmas de Inprovider Sign por cliente (Trello NtoUBU1R).
 *
 * El backend las devuelve en `GET /trackvision/lexgosign/status` → `quota`. `limit` null =
 * sin límite. La bolsa descuenta al quedar el documento firmado; los envíos en curso
 * reservan su lugar, por eso `remaining = limit - firmados - en curso`.
 *
 * El backend es quien bloquea: esto solo decide qué mostrar y si pedir el pago.
 */

export type LexgosignSignatureType = 'SIMPLE' | 'ADVANCED';

export interface LexgosignQuotaUsage {
  limit: number | null;
  signed: number;
  inProgress: number;
  remaining: number | null;
}

export interface LexgosignQuotaSummary {
  simple: LexgosignQuotaUsage;
  advanced: LexgosignQuotaUsage;
}

function usageFor(
  quota: LexgosignQuotaSummary | null | undefined,
  type: LexgosignSignatureType
): LexgosignQuotaUsage | null {
  if (!quota) return null;
  return (type === 'ADVANCED' ? quota.advanced : quota.simple) || null;
}

function hasQuota(quota: LexgosignQuotaSummary | null | undefined, type: LexgosignSignatureType): boolean {
  const u = usageFor(quota, type);
  return !!u && u.limit !== null && u.limit !== undefined;
}

/** Hay bolsa de ese tipo y no queda lugar. */
export function isQuotaExhausted(
  quota: LexgosignQuotaSummary | null | undefined,
  type: LexgosignSignatureType
): boolean {
  const u = usageFor(quota, type);
  return hasQuota(quota, type) && (u!.remaining ?? 0) <= 0;
}

/**
 * Solo la avanzada SIN bolsa se paga con tarjeta. Con bolsa es la prueba gratuita.
 * Sin datos de bolsa se comporta como antes: la avanzada se paga.
 */
export function needsPayment(
  quota: LexgosignQuotaSummary | null | undefined,
  type: LexgosignSignatureType
): boolean {
  return type === 'ADVANCED' && !hasQuota(quota, 'ADVANCED');
}

/** Texto para la tarjeta del tipo de firma, o null si no hay bolsa. */
export function quotaBadge(
  quota: LexgosignQuotaSummary | null | undefined,
  type: LexgosignSignatureType
): string | null {
  if (!hasQuota(quota, type)) return null;
  if (isQuotaExhausted(quota, type)) return 'Bolsa agotada';
  const n = usageFor(quota, type)!.remaining as number;
  return n === 1 ? 'Queda 1 en tu bolsa' : `Quedan ${n} en tu bolsa`;
}
